import { createContext, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { baseTr, type BaseMessageKey } from "./base-dictionary";

// The i18n MACHINERY (ADR 0015 §7) — a small typed provider, not a framework. It
// is GENERIC over the app's merged dictionary: the foundation owns the base keys
// and a provider/hook that resolve any string at runtime, while each app layers a
// typed `useT<AppMessageKey>()` on top so a missing key is a compile error.
export type Locale = "tr" | "en";
export type Dictionaries = { tr: Record<string, string>; en: Record<string, string> };

interface I18nValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({
  children,
  dictionaries,
  initial = "tr",
}: {
  children: ReactNode;
  dictionaries: Dictionaries;
  initial?: Locale;
}) {
  const [locale, setLocale] = useState<Locale>(initial);
  const value = useMemo<I18nValue>(() => {
    const dict = dictionaries[locale] ?? dictionaries.tr;
    return { locale, setLocale, t: (key) => dict[key] ?? dictionaries.tr[key] ?? key };
  }, [locale, dictionaries]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

// Typed hook. The foundation's own components call `useT()` (keys default to the
// base set); an app calls `useT<AppMessageKey>()` for its full, type-checked set.
export function useT<TKey extends string = BaseMessageKey>(): {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: TKey) => string;
} {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useT must be used within an I18nProvider");
  return ctx as { locale: Locale; setLocale: (l: Locale) => void; t: (key: TKey) => string };
}

export { baseTr };
